import { PdfTemplateRow, PdfTemplateValueStatus } from "@/data/models/ClientAdministrationRow";

/**
 * The admin portal's PDF template screens: ordering, search and the rules for what to flag. Pure,
 * so they are pinned by tests.
 */

/** Whether a value fails to fill its field: it prints blank, or its checkbox is always left unticked. */
export function isProblemStatus(status: PdfTemplateValueStatus): boolean {
  return status !== "RENDERED";
}

/** A case-insensitive substring of the name or file name; blank matches everything. */
export function matchesPdfTemplateSearch(row: PdfTemplateRow, query: string): boolean {
  const needle = query.trim().toLocaleLowerCase();

  return (
    needle === "" ||
    [row.name, row.fileName].some((value) => value !== null && value.toLocaleLowerCase().includes(needle))
  );
}

/**
 * Search, then sort: the template the HyDry certificate uses first, then by name in German order
 * (falling back to the file name), so the one that matters is on top.
 */
export function selectPdfTemplateRows(
  rows: PdfTemplateRow[],
  query: string,
  hyDryCertificateTemplateId: string | null
): PdfTemplateRow[] {
  const title = (row: PdfTemplateRow) => row.name ?? row.fileName ?? "";

  return rows
    .filter((row) => matchesPdfTemplateSearch(row, query))
    .sort(
      (a, b) =>
        Number(b.id === hyDryCertificateTemplateId) - Number(a.id === hyDryCertificateTemplateId) ||
        title(a).localeCompare(title(b), "de", { sensitivity: "base", numeric: true }) ||
        a.id.localeCompare(b.id)
    );
}

/** The prefix every HyDry certificate template's file name starts with, as IRES Cloud picks it. */
export const HYDRY_CERTIFICATE_PREFIX = "-HyDry";

/**
 * A client template that looks like a certificate template but is not the one used: only the first
 * by document id is, so a second one is silently ignored and worth pointing out.
 */
export function isIgnoredCertificateTemplate(row: PdfTemplateRow, hyDryCertificateTemplateId: string | null): boolean {
  return (
    hyDryCertificateTemplateId !== null &&
    row.id !== hyDryCertificateTemplateId &&
    (row.fileName ?? "").startsWith(HYDRY_CERTIFICATE_PREFIX)
  );
}
